import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, FileOutput, BookOpen, LogOut, FileText, User, FilePlus2, Settings } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { to: "/", label: "Templates", icon: LayoutDashboard },
    { to: "/generate", label: "Generate", icon: FilePlus2 },
    { to: "/documents", label: "Documents", icon: FileOutput },
    { to: "/api-docs", label: "API Docs", icon: BookOpen },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/80" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5" data-testid="app-logo">
              <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                <FileText size={16} strokeWidth={1.5} />
              </div>
              <span className="font-heading font-bold text-sm tracking-tight hidden sm:block">
                PDF Templates
              </span>
            </Link>
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link key={item.to} to={item.to}>
                    <Button
                      variant={active ? "secondary" : "ghost"}
                      size="sm"
                      className={`gap-1.5 text-xs h-8 ${active ? "font-medium" : "text-slate-500"}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <Icon size={14} strokeWidth={1.5} />
                      <span className="hidden sm:inline">{item.label}</span>
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-xs h-8" data-testid="user-menu-trigger">
                <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                  <User size={12} strokeWidth={1.5} />
                </div>
                <span className="hidden sm:inline">{user?.email || user?.name || "Admin"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="text-xs text-slate-500">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="gap-2 text-red-600" data-testid="logout-button">
                <LogOut size={14} />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
